import {ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {CdkDragDrop, DragDropModule} from '@angular/cdk/drag-drop';
import {faChevronLeft, faChevronRight, faLayerGroup, faPlus} from '@fortawesome/free-solid-svg-icons';
import {PageHeaderService} from '../../services/page-header/page-header.service';
import {UiModalComponent} from '../../shared/ui';
import {MealPlanStore} from '../../services/meal-plan/meal-plan-store.service';
import {MealPlanEntry, RecipeSearchResult, Slot} from '../../services/meal-plan/meal-plan.models';
import {keyToLocalDate} from '../../utils/date-key.util';
import {EntryCardComponent} from './entry-card/entry-card.component';
import {SlotPickerComponent} from './slot-picker/slot-picker.component';

/** A grid cell drop target. */
interface CellDrop {
  dateKey: string;
  slot: Slot;
}
/** The pool drop target (sentinel — the pool has no date/slot). */
interface PoolDrop {
  pool: true;
}
type DropData = CellDrop | PoolDrop | undefined;

function isPoolDrop(data: DropData): data is PoolDrop {
  return !!data && 'pool' in data;
}

@Component({
  selector: 'app-meal-planner',
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule, DragDropModule, UiModalComponent, EntryCardComponent, SlotPickerComponent],
  templateUrl: './meal-planner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MealPlannerComponent implements OnInit, OnDestroy {
  protected readonly store = inject(MealPlanStore);
  private readonly router = inject(Router);
  private readonly pageHeader = inject(PageHeaderService);

  @ViewChild(SlotPickerComponent) private picker!: SlotPickerComponent;
  @ViewChild('editModal') private editModal!: UiModalComponent;

  protected readonly faChevronLeft = faChevronLeft;
  protected readonly faChevronRight = faChevronRight;
  protected readonly faPlus = faPlus;
  protected readonly faLayerGroup = faLayerGroup;

  /** Empty slots the user manually expanded into a full row for this session. */
  protected readonly expandedSlots = signal<Set<Slot>>(new Set());

  /**
   * True from the moment a card drag passes the CDK threshold until just after
   * it ends. Guards the recipe-open click: dropping a card synthesizes a `click`
   * on whatever card sits under the pointer, which would otherwise navigate.
   */
  private dragging = false;

  // --- Edit modal state ---
  protected readonly editing = signal<MealPlanEntry | null>(null);
  protected editServings: number | null = null;
  protected editNote = '';

  ngOnInit(): void {
    this.pageHeader.setPageTitle('Menüplan');
  }

  ngOnDestroy(): void {
    this.pageHeader.clearAll();
  }

  /**
   * Slot keys rendered as full rows: every `primary` slot, plus any `secondary`/
   * `custom` slot that has an entry in the window or was manually expanded. Order
   * follows the server-owned catalogue.
   */
  get rowSlots(): Slot[] {
    const withEntries = this.store.slotKeysWithEntries();
    const expanded = this.expandedSlots();
    return this.store
      .slots()
      .filter((s) => s.category === 'primary' || withEntries.has(s.key) || expanded.has(s.key))
      .map((s) => s.key);
  }

  /** Unused secondary/custom slots shown as a thin "+ label" affordance instead of a full row. */
  get collapsedSlots(): Slot[] {
    const shown = new Set(this.rowSlots);
    return this.store
      .slots()
      .filter((s) => !shown.has(s.key))
      .map((s) => s.key);
  }

  expandSlot(slot: Slot): void {
    this.expandedSlots.update((set) => new Set(set).add(slot));
  }

  entriesFor(dateKey: string, slot: Slot): MealPlanEntry[] {
    return this.store.grid().get(dateKey)?.get(slot) ?? [];
  }

  /** An empty cell doubles as its own "add meal" button (whole-cell click). */
  isCellEmpty(dateKey: string, slot: Slot): boolean {
    return this.entriesFor(dateKey, slot).length === 0;
  }

  /** Whole-cell add: fires only for empty cells so clicks on meals never add. */
  onEmptyCellClick(dateKey: string, slot: Slot): void {
    if (this.isCellEmpty(dateKey, slot)) this.openPicker(dateKey, slot);
  }

  /** Only persisted entries can be dragged; a pending/failed create isn't movable. */
  canDrag(entry: MealPlanEntry): boolean {
    return entry.id != null && !entry.pending && !entry.failed;
  }

  onDragStarted(): void {
    this.dragging = true;
  }

  /**
   * The drag's mouseup synthesizes a `click` that fires immediately after this
   * handler, so keep the guard up until the next macrotask to swallow it.
   */
  onDragEnded(): void {
    setTimeout(() => (this.dragging = false));
  }

  /**
   * A card was dropped. Four cases, distinguished by source and target drop list:
   * pool → cell = place, cell → cell = move, cell → pool = un-place, pool → pool
   * = no-op. The store handles the persistence for each.
   */
  onDrop(event: CdkDragDrop<DropData>): void {
    const entry = event.item.data as MealPlanEntry | undefined;
    if (!entry) return;
    const target = event.container.data;
    const fromPool = isPoolDrop(event.previousContainer.data);

    if (isPoolDrop(target)) {
      if (!fromPool) this.store.unplaceToPool(entry);
      return;
    }
    if (target) {
      if (fromPool) this.store.placeFromPool(entry, target.dateKey, target.slot);
      else this.store.moveEntry(entry, target.dateKey, target.slot);
    }
  }

  isToday(dateKey: string): boolean {
    return dateKey === this.store.todayKey;
  }

  isPast(dateKey: string): boolean {
    return dateKey < this.store.todayKey;
  }

  // --- Date formatting (locale supplies the words; de-CH registered in app.module) ---

  private fmt(dateKey: string, opts: Intl.DateTimeFormatOptions): string {
    return new Intl.DateTimeFormat('de-CH', opts).format(keyToLocalDate(dateKey));
  }

  formatLong(dateKey: string): string {
    return this.fmt(dateKey, {weekday: 'long', day: 'numeric', month: 'long'});
  }

  formatWeekdayShort(dateKey: string): string {
    return this.fmt(dateKey, {weekday: 'short'});
  }

  formatDayMonth(dateKey: string): string {
    return this.fmt(dateKey, {day: 'numeric', month: 'long'});
  }

  get rangeLabel(): string {
    return `${this.formatDayMonth(this.store.from())} – ${this.formatDayMonth(this.store.to())}`;
  }

  a11yAddLabel(dateKey: string, slot: Slot): string {
    return `Mahlzeit hinzufügen, ${this.formatLong(dateKey)}, ${this.store.slotLabel(slot)}`;
  }

  // --- Add / edit / delete ---

  openPicker(dateKey: string, slot: Slot): void {
    this.picker.open(dateKey, slot);
  }

  /** Open the picker to add a candidate to the pool (no target cell). */
  openPoolPicker(): void {
    this.picker.openForPool();
  }

  onRecipeChosen(e: {dateKey: string; slot: Slot; recipe: RecipeSearchResult}): void {
    this.store.addRecipe(e.dateKey, e.slot, e.recipe);
  }

  onFreeTextChosen(e: {dateKey: string; slot: Slot; title: string}): void {
    this.store.addFreeText(e.dateKey, e.slot, e.title);
  }

  onPoolItemChosen(e: {dateKey: string; slot: Slot; entry: MealPlanEntry}): void {
    this.store.placeFromPool(e.entry, e.dateKey, e.slot);
  }

  onRecipePooled(recipe: RecipeSearchResult): void {
    this.store.addRecipeToPool(recipe);
  }

  onFreeTextPooled(title: string): void {
    this.store.addFreeTextToPool(title);
  }

  openEdit(entry: MealPlanEntry): void {
    this.editing.set(entry);
    this.editServings = entry.servings;
    this.editNote = entry.note ?? '';
    this.editModal.open();
  }

  saveEdit(): void {
    const entry = this.editing();
    if (!entry) return;
    this.store.updateEntry(entry, {servings: this.editServings, note: this.editNote.trim()});
    this.editModal.close();
    this.editing.set(null);
  }

  deleteEntry(entry: MealPlanEntry): void {
    this.store.deleteEntry(entry);
  }

  retry(entry: MealPlanEntry): void {
    // Re-issue the failed create in place (grid cell or pool). The store knows
    // which from the entry's placement.
    this.store.retry(entry);
  }

  openRecipe(entry: MealPlanEntry): void {
    if (this.dragging) return;
    if (!entry.recipe) return;
    // Carry the planned portion size so the recipe opens pre-scaled. Only when
    // it differs from the recipe's base — the recipe page drops a no-op param
    // anyway, but this keeps the URL clean.
    const servings =
      entry.servings != null && entry.servings !== entry.recipe.servings ? entry.servings : null;
    this.router.navigate(['/recipe', entry.recipe.id], {
      queryParams: servings != null ? {servings} : undefined,
    });
  }
}
